import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { ChevronLeft, ChevronRight, ArrowLeft, Calendar, User, Tag, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { publicApi } from "@/lib/api/public";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";

export const Route = createFileRoute("/portfolio/$id")({
  component: PortfolioDetailPage,
  head: () => ({
    meta: [
      { title: "Project Details | BAZZAR 3D" },
      { name: "description", content: "Project details with before/after images." },
    ],
  }),
});

type Project = {
  id: string;
  titleAr: string;
  titleEn: string;
  category: string;
  cover: string;
  beforeImage: string;
  afterImage: string;
  descriptionAr: string;
  descriptionEn: string;
  tech: string[];
  clientName: string;
  year: string;
  link: string;
  gallery: string[];
};

function mapItem(it: any): Project {
  const gallery: string[] = [];
  const raw = it.gallery ?? it.images ?? it.photos ?? [];
  if (Array.isArray(raw)) {
    raw.forEach((g: any) => {
      const url = typeof g === "string" ? g : (g?.url ?? g?.src ?? "");
      if (url) gallery.push(url);
    });
  }
  return {
    id: String(it.id ?? ""),
    titleAr: it.titleAr ?? it.title_ar ?? "",
    titleEn: it.titleEn ?? it.title_en ?? "",
    category: it.category ?? "",
    cover: it.cover ?? it.image ?? "",
    beforeImage: it.beforeImage ?? it.before_image ?? "",
    afterImage: it.afterImage ?? it.after_image ?? "",
    descriptionAr: it.descriptionAr ?? it.description_ar ?? "",
    descriptionEn: it.descriptionEn ?? it.description_en ?? "",
    tech: it.tech ?? [],
    clientName: it.clientName ?? it.client_name ?? it.client ?? "",
    year: it.year ?? "",
    link: it.link ?? "",
    gallery,
  };
}

function PortfolioDetailPage() {
  const { id } = Route.useParams();
  const { dir, lang } = useLang();
  const L = (a: string, e: string) => (lang === "en" ? e : a);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const loadFromList = () => publicApi.getPortfolio().then((r: any) => {
      if (cancelled) return;
      const items = r?.data?.items ?? r?.items ?? [];
      const found = items.find((x: any) => String(x.id) === String(id));
      setProject(found ? mapItem(found) : null);
    });

    publicApi.getPortfolioItem(id)
      .then((res: any) => {
        if (cancelled) return;
        const it = res?.data?.item ?? res?.data ?? res?.item ?? null;
        if (it && (it.id || it.titleAr || it.titleEn)) {
          setProject(mapItem(it));
        } else {
          return loadFromList();
        }
      })
      .catch(() => loadFromList())
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Slider only shows gallery + cover (Before/After is its own section)
  const slides: { type: "img"; src: string }[] = [];
  if (project) {
    if (project.cover && project.cover !== project.afterImage) {
      slides.push({ type: "img", src: project.cover });
    }
    project.gallery.forEach((g) => slides.push({ type: "img", src: g }));
    if (slides.length === 0 && project.afterImage) slides.push({ type: "img", src: project.afterImage });
    if (slides.length === 0 && project.cover) slides.push({ type: "img", src: project.cover });
  }
  const hasBeforeAfter = !!(project?.beforeImage && project?.afterImage);

  const title = project ? (lang === "en" ? (project.titleEn || project.titleAr) : (project.titleAr || project.titleEn)) : "";
  const desc = project ? (lang === "en" ? (project.descriptionEn || project.descriptionAr) : (project.descriptionAr || project.descriptionEn)) : "";

  const next = () => setSlideIdx((i) => (i + 1) % Math.max(slides.length, 1));
  const prev = () => setSlideIdx((i) => (i - 1 + Math.max(slides.length, 1)) % Math.max(slides.length, 1));

  return (
    <div className="flex min-h-screen flex-col bg-background" dir={dir}>
      <SiteHeader />
      <main className="flex-1">
        {loading ? (
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
            <div className="aspect-[16/9] animate-pulse rounded-3xl bg-secondary/60" />
          </div>
        ) : !project ? (
          <div className="mx-auto max-w-3xl px-4 py-10 text-center sm:px-6">
            <div className="rounded-3xl border border-dashed border-border bg-white p-12">
              <p className="text-base font-bold">{L("المشروع غير موجود", "Project not found")}</p>
              <Link to="/portfolio" className="mt-4 inline-flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-xs font-bold text-primary-foreground">
                {L("استعرض كل المشاريع", "Browse all projects")}
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Hero band — single line */}
            <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
              <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-start">
                  <Link to="/portfolio" className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline">
                    <ChevronLeft className={`h-4 w-4 ${dir === "ltr" ? "rotate-180" : ""}`} />
                    {L("العودة لكل الأعمال", "Back to all projects")}
                  </Link>

                  <span className="hidden h-5 w-px bg-border sm:block" />

                  {project.category && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-bold text-primary">
                      <Tag className="h-3 w-3" />
                      {project.category}
                    </span>
                  )}

                  <h1 className="min-w-0 flex-1 truncate text-xl font-extrabold leading-tight text-foreground sm:text-2xl">
                    {title}
                  </h1>

                  {project.link && project.link !== "#" && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-white px-3.5 text-[11px] font-bold text-foreground shadow-sm transition hover:border-primary hover:text-primary"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {L("زيارة الرابط", "Visit link")}
                    </a>
                  )}
                </div>
              </div>
            </section>

            {/* Body */}
            <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
              <div className="grid gap-8 lg:grid-cols-3">
                {/* Slider — main */}
                <div className="lg:col-span-2">
                  {slides.length > 0 && (
                    <>
                      <div className="relative aspect-[16/10] overflow-hidden rounded-3xl border border-border bg-secondary/30 shadow-sm">
                        <img src={slides[slideIdx]?.src} alt={title} className="h-full w-full object-cover" />

                        {slides.length > 1 && (
                          <>
                            <button
                              onClick={prev}
                              aria-label="prev"
                              className="absolute top-1/2 left-3 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-primary shadow-md hover:bg-white"
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                              onClick={next}
                              aria-label="next"
                              className="absolute top-1/2 right-3 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-primary shadow-md hover:bg-white"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                            <div className="absolute bottom-3 left-1/2 z-30 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/50 px-3 py-1 text-[11px] font-bold text-white backdrop-blur-sm" data-ltr-number>
                              {slideIdx + 1} / {slides.length}
                            </div>
                          </>
                        )}
                      </div>

                      {slides.length > 1 && (
                        <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-6">
                          {slides.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => setSlideIdx(i)}
                              className={`relative aspect-[4/3] overflow-hidden rounded-xl border-2 transition ${
                                i === slideIdx ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"
                              }`}
                            >
                              <img src={s.src} alt="" className="h-full w-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Description */}
                  {desc && (
                    <div className="mt-10 rounded-3xl border border-border bg-white p-6 sm:p-8">
                      <h2 className="text-lg font-extrabold text-foreground sm:text-xl">
                        {L("عن المشروع", "About the project")}
                      </h2>
                      <div className="mt-3 h-1 w-12 rounded-full bg-primary" />
                      <p className="mt-5 whitespace-pre-line text-sm leading-8 text-muted-foreground sm:text-base">
                        {desc}
                      </p>
                    </div>
                  )}
                </div>

                {/* Side info */}
                <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
                  <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      {L("تفاصيل المشروع", "Project details")}
                    </h3>
                    <dl className="mt-4 divide-y divide-border">
                      {project.category && (
                        <div className="flex items-center justify-between gap-3 py-3">
                          <dt className="inline-flex items-center gap-2 text-xs font-bold text-muted-foreground">
                            <Tag className="h-3.5 w-3.5 text-primary" />
                            {L("التصنيف", "Category")}
                          </dt>
                          <dd className="text-sm font-extrabold text-foreground">{project.category}</dd>
                        </div>
                      )}
                    </dl>

                    {project.tech.length > 0 && (
                      <div className="mt-5 border-t border-border pt-5">
                        <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          {L("التقنيات المستخدمة", "Tech used")}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {project.tech.map((t, i) => (
                            <span key={i} className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold text-primary">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <Link
                    to={"/quote" as any}
                    className="group flex items-center justify-between gap-3 rounded-3xl bg-primary p-6 text-primary-foreground shadow-md transition hover:-translate-y-0.5"
                  >
                    <div className="text-start">
                      <div className="text-xs font-bold opacity-80">
                        {L("معجب بالعمل؟", "Like this work?")}
                      </div>
                      <div className="mt-1 text-sm font-extrabold sm:text-base">
                        {L("اطلب مشروع مشابه", "Request a similar project")}
                      </div>
                    </div>
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 transition group-hover:bg-white/25">
                      <ArrowLeft className={`h-4 w-4 ${dir === "ltr" ? "rotate-180" : ""}`} />
                    </span>
                  </Link>
                </aside>
              </div>
            </section>

            {/* Before / After — separate section */}
            {hasBeforeAfter && (
              <section className="border-t border-border bg-secondary/30">
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
                  <div className="text-start">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold text-primary">
                      {L("قبل / بعد", "Before / After")}
                    </span>
                    <h2 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
                      {L("شوف الفرق بنفسك", "See the difference")}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {L("اسحب الشريط لاستعراض المشهد قبل وبعد التنفيذ.", "Drag the slider to compare before and after.")}
                    </p>
                  </div>
                  <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
                    <div className="aspect-[16/9]">
                      <BeforeAfterSlider
                        before={project.beforeImage}
                        after={project.afterImage}
                        alt={title}
                        className="h-full w-full"
                      />
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
